import { FC } from "react";

import { formatCurrencyFromCents } from "@simplyk/common";

import { PrizeValueRestriction } from "../helpers/types";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { SWEEPSTAKES_LAWS_BY_STATE_URL } from "@/features/LiveFormEditor/common/sweepstakes";
import { ExternalLink } from "@/icons/outlined";

interface PrizeValueLimitDialogProps {
  restriction: PrizeValueRestriction | null;
  onEditPrizes: () => void;
  onBackToDashboard: () => void;
  inline?: boolean;
}

export const PrizeValueLimitDialog: FC<PrizeValueLimitDialogProps> = ({
  restriction,
  onEditPrizes,
  onBackToDashboard,
  inline = false,
}) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();
  const { organizationCurrency } = useCurrentUserContext();

  const state = restriction?.regionName ?? "";
  const total = formatCurrencyFromCents(restriction?.totalPrizeValue ?? 0, {
    isoLocale,
    currency: organizationCurrency,
  });

  return (
    <DashboardDialog open={Boolean(restriction)} inline={inline} onClose={onEditPrizes} vibe="warning" maxWidth="xs">
      <DashboardDialogBody
        slots={{
          header: (
            <DashboardDialogHeader
              title={t("dashboard", "liveFormEditor.stepPrizes.valueLimitDialog.title", { state })}
              sx={(theme) => ({ padding: theme.spacing(3, 2, 1.5, 3) })}
            />
          ),
          content: (
            <DashboardDialogContent sx={{ paddingTop: 0 }}>
              <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                <Trans
                  space="dashboard"
                  i18nKey="liveFormEditor.stepPrizes.valueLimitDialog.body"
                  values={{ state, total }}
                  components={{ bold: <strong /> }}
                />
              </DashboardTypography>
              <DashboardButton
                variant="text"
                vibe="brand"
                href={SWEEPSTAKES_LAWS_BY_STATE_URL}
                target="_blank"
                endIcon={<ExternalLink />}
                data-test="prize-value-limit-learn-more"
                sx={{ paddingX: 0, minWidth: 0 }}
              >
                {t("dashboard", "liveFormEditor.stepPrizes.valueLimitDialog.learnMore")}
              </DashboardButton>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter sx={(theme) => ({ padding: theme.spacing(2) })}>
              <DashboardButton
                variant="filled"
                vibe="warning"
                type="button"
                onClick={onEditPrizes}
                data-test="prize-value-limit-edit-prizes"
              >
                {t("dashboard", "liveFormEditor.stepPrizes.valueLimitDialog.editPrizes")}
              </DashboardButton>
              <DashboardButton variant="outlined" vibe="neutral" onClick={onBackToDashboard}>
                {t("dashboard", "liveFormEditor.stepPrizes.valueLimitDialog.backToDashboard")}
              </DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
